/**
 * Site locales.
 *
 * Simplified Chinese is the root locale and also owns the engineering
 * documents (design, plan, ADRs, runbooks). Those are working documents rather
 * than product pages, so every locale links to them at their root paths and
 * labels them as Simplified Chinese instead of shipping stale translations.
 */
export interface SiteLocale {
  /** Directory and URL segment; the root locale has an empty prefix. */
  readonly path: string;
  /** BCP 47 tag written to `<html lang>`. */
  readonly lang: string;
  /** Writing direction written to `<html dir>`. */
  readonly dir?: "rtl";
  /** Name shown in the language menu, in that language. */
  readonly label: string;
  readonly description: string;
  readonly ui: {
    readonly guide: string;
    readonly api: string;
    readonly playground: string;
    readonly gettingStarted: string;
    readonly tsx: string;
    readonly architecture: string;
    readonly scrolling: string;
    readonly editing: string;
    readonly events: string;
    readonly accessibility: string;
    readonly components: string;
    readonly preview: string;
    readonly code: string;
    readonly previewError: string;
    readonly styling: string;
    readonly scssLess: string;
    readonly cssSupport: string;
    readonly migration: string;
    readonly diagnostics: string;
    readonly sectionStyling: string;
    readonly sectionBaseElements: string;
    readonly elements: string;
    readonly elementsEditing: string;
    readonly elementsSvg: string;
    readonly widgets: string;
    readonly groupForm: string;
    readonly groupLayout: string;
    readonly groupOverlay: string;
    readonly groupData: string;
    readonly groupFeedback: string;
    readonly groupProduct: string;
    readonly sectionStart: string;
    readonly sectionCapabilities: string;
    readonly sectionShipping: string;
    readonly changelog: string;
    readonly publicApi: string;
    readonly outline: string;
    readonly returnToTop: string;
    readonly appearance: string;
    readonly lastUpdated: string;
    readonly previousPage: string;
    readonly nextPage: string;
    readonly menu: string;
    readonly sidebarMenu: string;
    readonly darkMode: string;
    readonly languageMenu: string;
    readonly footerMessage: string;
    readonly searchButton: string;
    readonly searchPlaceholder: string;
    readonly searchNoResults: string;
    readonly searchResetButton: string;
    readonly searchFooterSelect: string;
    readonly searchFooterNavigate: string;
    readonly searchFooterClose: string;
  };
}

export const SITE_LOCALES: readonly SiteLocale[] = [
  {
    path: "",
    lang: "zh-Hans",
    label: "简体中文",
    description: "面向高性能交互、虚拟滚动与 canvas 原生编辑的 Web 渲染引擎",
    ui: {
      guide: "指南",
      api: "API",
      playground: "Playground",
      gettingStarted: "快速开始",
      tsx: "TSX",
      architecture: "架构概览",
      scrolling: "滚动与虚拟化",
      editing: "文本与编辑",
      events: "事件与命中",
      accessibility: "无障碍与测试",
      components: "组件",
      preview: "预览",
      code: "代码",
      previewError: "预览加载失败",
      styling: "样式指南",
      scssLess: "SCSS 与 Less",
      cssSupport: "CSS 支持表",
      migration: "迁移",
      diagnostics: "诊断",
      sectionStyling: "样式",
      sectionBaseElements: "基础组件",
      elements: "视图、文本与图片",
      elementsEditing: "输入与编辑",
      elementsSvg: "SVG 与矢量图形",
      widgets: "Widgets",
      groupForm: "表单",
      groupLayout: "布局",
      groupOverlay: "浮层",
      groupData: "数据",
      groupFeedback: "反馈",
      groupProduct: "业务组件",
      sectionStart: "入门",
      sectionCapabilities: "能力",
      sectionShipping: "上线",
      changelog: "变更日志",
      publicApi: "公开 API",
      outline: "本页目录",
      returnToTop: "回到顶部",
      appearance: "外观",
      lastUpdated: "最后更新",
      previousPage: "上一页",
      nextPage: "下一页",
      menu: "菜单",
      sidebarMenu: "侧边栏菜单",
      darkMode: "深色模式",
      languageMenu: "选择语言",
      footerMessage: "以 Apache-2.0 许可发布",
      searchButton: "搜索文档",
      searchPlaceholder: "搜索文档",
      searchNoResults: "没有找到结果",
      searchResetButton: "清除查询条件",
      searchFooterSelect: "选择",
      searchFooterNavigate: "切换",
      searchFooterClose: "关闭",
    },
  },
  {
    path: "zh-Hant",
    lang: "zh-Hant",
    label: "繁體中文",
    description: "為高效能互動、虛擬捲動與 canvas 原生編輯打造的 Web 繪製引擎",
    ui: {
      guide: "指南",
      api: "API",
      playground: "Playground",
      gettingStarted: "快速開始",
      tsx: "TSX",
      architecture: "架構概覽",
      scrolling: "捲動與虛擬化",
      editing: "文字與編輯",
      events: "事件與命中",
      accessibility: "無障礙與測試",
      components: "元件",
      preview: "預覽",
      code: "程式碼",
      previewError: "預覽載入失敗",
      styling: "樣式指南",
      scssLess: "SCSS 與 Less",
      cssSupport: "CSS 支援表",
      migration: "遷移",
      diagnostics: "診斷",
      sectionStyling: "樣式",
      sectionBaseElements: "基礎元件",
      elements: "視圖、文字與圖片",
      elementsEditing: "輸入與編輯",
      elementsSvg: "SVG 與向量圖形",
      widgets: "Widgets",
      groupForm: "表單",
      groupLayout: "佈局",
      groupOverlay: "浮層",
      groupData: "資料",
      groupFeedback: "回饋",
      groupProduct: "業務元件",
      sectionStart: "入門",
      sectionCapabilities: "能力",
      sectionShipping: "上線",
      changelog: "變更紀錄",
      publicApi: "公開 API",
      outline: "本頁目錄",
      returnToTop: "回到頂部",
      appearance: "外觀",
      lastUpdated: "最後更新",
      previousPage: "上一頁",
      nextPage: "下一頁",
      menu: "選單",
      sidebarMenu: "側邊欄選單",
      darkMode: "深色模式",
      languageMenu: "選擇語言",
      footerMessage: "以 Apache-2.0 授權發佈",
      searchButton: "搜尋文件",
      searchPlaceholder: "搜尋文件",
      searchNoResults: "找不到結果",
      searchResetButton: "清除搜尋條件",
      searchFooterSelect: "選擇",
      searchFooterNavigate: "切換",
      searchFooterClose: "關閉",
    },
  },
  {
    path: "ja",
    lang: "ja",
    label: "日本語",
    description:
      "高性能なインタラクション、仮想スクロール、canvas ネイティブ編集のための Web レンダリングエンジン",
    ui: {
      guide: "ガイド",
      api: "API",
      playground: "Playground",
      gettingStarted: "はじめに",
      tsx: "TSX",
      architecture: "アーキテクチャ概要",
      scrolling: "スクロールと仮想化",
      editing: "テキストと編集",
      events: "イベントとヒットテスト",
      accessibility: "アクセシビリティとテスト",
      components: "コンポーネント",
      preview: "プレビュー",
      code: "コード",
      previewError: "プレビューの読み込みに失敗しました",
      styling: "スタイリング",
      scssLess: "SCSS と Less",
      cssSupport: "CSS サポート表",
      migration: "移行",
      diagnostics: "診断",
      sectionStyling: "スタイル",
      sectionBaseElements: "基本要素",
      elements: "View・Text・Image",
      elementsEditing: "入力と編集",
      elementsSvg: "SVG とベクター図形",
      widgets: "Widgets",
      groupForm: "フォーム",
      groupLayout: "レイアウト",
      groupOverlay: "オーバーレイ",
      groupData: "データ",
      groupFeedback: "フィードバック",
      groupProduct: "プロダクト",
      sectionStart: "入門",
      sectionCapabilities: "機能",
      sectionShipping: "リリース",
      changelog: "変更履歴",
      publicApi: "公開 API",
      outline: "このページの目次",
      returnToTop: "トップへ戻る",
      appearance: "外観",
      lastUpdated: "最終更新",
      previousPage: "前のページ",
      nextPage: "次のページ",
      menu: "メニュー",
      sidebarMenu: "サイドバーメニュー",
      darkMode: "ダークモード",
      languageMenu: "言語を選択",
      footerMessage: "Apache-2.0 ライセンスで公開",
      searchButton: "ドキュメントを検索",
      searchPlaceholder: "ドキュメントを検索",
      searchNoResults: "結果が見つかりません",
      searchResetButton: "検索条件をクリア",
      searchFooterSelect: "選択",
      searchFooterNavigate: "移動",
      searchFooterClose: "閉じる",
    },
  },
  {
    path: "ko",
    lang: "ko",
    label: "한국어",
    description: "고성능 상호작용, 가상 스크롤, canvas 네이티브 편집을 위한 웹 렌더링 엔진",
    ui: {
      guide: "가이드",
      api: "API",
      playground: "Playground",
      gettingStarted: "시작하기",
      tsx: "TSX",
      architecture: "아키텍처 개요",
      scrolling: "스크롤과 가상화",
      editing: "텍스트와 편집",
      events: "이벤트와 히트 테스트",
      accessibility: "접근성과 테스트",
      components: "컴포넌트",
      preview: "미리보기",
      code: "코드",
      previewError: "미리보기를 불러오지 못했습니다",
      styling: "스타일링",
      scssLess: "SCSS와 Less",
      cssSupport: "CSS 지원 표",
      migration: "마이그레이션",
      diagnostics: "진단",
      sectionStyling: "스타일",
      sectionBaseElements: "기본 요소",
      elements: "View·Text·Image",
      elementsEditing: "입력과 편집",
      elementsSvg: "SVG와 벡터 그래픽",
      widgets: "Widgets",
      groupForm: "폼",
      groupLayout: "레이아웃",
      groupOverlay: "오버레이",
      groupData: "데이터",
      groupFeedback: "피드백",
      groupProduct: "프로덕트",
      sectionStart: "입문",
      sectionCapabilities: "기능",
      sectionShipping: "배포",
      changelog: "변경 내역",
      publicApi: "공개 API",
      outline: "이 페이지 목차",
      returnToTop: "맨 위로",
      appearance: "테마",
      lastUpdated: "마지막 업데이트",
      previousPage: "이전 페이지",
      nextPage: "다음 페이지",
      menu: "메뉴",
      sidebarMenu: "사이드바 메뉴",
      darkMode: "다크 모드",
      languageMenu: "언어 선택",
      footerMessage: "Apache-2.0 라이선스로 배포",
      searchButton: "문서 검색",
      searchPlaceholder: "문서 검색",
      searchNoResults: "결과가 없습니다",
      searchResetButton: "검색어 지우기",
      searchFooterSelect: "선택",
      searchFooterNavigate: "이동",
      searchFooterClose: "닫기",
    },
  },
  {
    path: "es",
    lang: "es",
    label: "Español",
    description:
      "Motor de renderizado web para interacción de alto rendimiento, scroll virtual y edición de texto nativa en canvas",
    ui: {
      guide: "Guía",
      api: "API",
      playground: "Playground",
      gettingStarted: "Primeros pasos",
      tsx: "TSX",
      architecture: "Arquitectura",
      scrolling: "Scroll y virtualización",
      editing: "Texto y edición",
      events: "Eventos y hit testing",
      accessibility: "Accesibilidad y pruebas",
      components: "Componentes",
      preview: "Vista previa",
      code: "Código",
      previewError: "No se pudo cargar la vista previa",
      styling: "Guía de estilos",
      scssLess: "SCSS y Less",
      cssSupport: "Tabla de soporte de CSS",
      migration: "Migración",
      diagnostics: "Diagnóstico",
      sectionStyling: "Estilos",
      sectionBaseElements: "Elementos básicos",
      elements: "View, Text e Image",
      elementsEditing: "Entrada y edición",
      elementsSvg: "SVG y gráficos vectoriales",
      widgets: "Widgets",
      groupForm: "Formularios",
      groupLayout: "Diseño",
      groupOverlay: "Superposiciones",
      groupData: "Datos",
      groupFeedback: "Retroalimentación",
      groupProduct: "Producto",
      sectionStart: "Introducción",
      sectionCapabilities: "Capacidades",
      sectionShipping: "Puesta en producción",
      changelog: "Registro de cambios",
      publicApi: "API pública",
      outline: "En esta página",
      returnToTop: "Volver arriba",
      appearance: "Apariencia",
      lastUpdated: "Última actualización",
      previousPage: "Anterior",
      nextPage: "Siguiente",
      menu: "Menú",
      sidebarMenu: "Menú lateral",
      darkMode: "Modo oscuro",
      languageMenu: "Cambiar idioma",
      footerMessage: "Publicado bajo la licencia Apache-2.0",
      searchButton: "Buscar en la documentación",
      searchPlaceholder: "Buscar en la documentación",
      searchNoResults: "Sin resultados para",
      searchResetButton: "Borrar la búsqueda",
      searchFooterSelect: "seleccionar",
      searchFooterNavigate: "navegar",
      searchFooterClose: "cerrar",
    },
  },
  {
    path: "fr",
    lang: "fr",
    label: "Français",
    description:
      "Moteur de rendu web pour l'interaction haute performance, le défilement virtuel et l'édition de texte native dans le canvas",
    ui: {
      guide: "Guide",
      api: "API",
      playground: "Playground",
      gettingStarted: "Démarrage",
      tsx: "TSX",
      architecture: "Architecture",
      scrolling: "Défilement et virtualisation",
      editing: "Texte et édition",
      events: "Événements et hit testing",
      accessibility: "Accessibilité et tests",
      components: "Composants",
      preview: "Aperçu",
      code: "Code",
      previewError: "Échec du chargement de l'aperçu",
      styling: "Guide des styles",
      scssLess: "SCSS et Less",
      cssSupport: "Tableau de prise en charge CSS",
      migration: "Migration",
      diagnostics: "Diagnostic",
      sectionStyling: "Styles",
      sectionBaseElements: "Éléments de base",
      elements: "View, Text et Image",
      elementsEditing: "Saisie et édition",
      elementsSvg: "SVG et graphiques vectoriels",
      widgets: "Widgets",
      groupForm: "Formulaires",
      groupLayout: "Mise en page",
      groupOverlay: "Surcouches",
      groupData: "Données",
      groupFeedback: "Retour",
      groupProduct: "Produit",
      sectionStart: "Débuter",
      sectionCapabilities: "Capacités",
      sectionShipping: "Mise en production",
      changelog: "Journal des modifications",
      publicApi: "API publique",
      outline: "Sur cette page",
      returnToTop: "Retour en haut",
      appearance: "Apparence",
      lastUpdated: "Dernière mise à jour",
      previousPage: "Précédent",
      nextPage: "Suivant",
      menu: "Menu",
      sidebarMenu: "Menu latéral",
      darkMode: "Mode sombre",
      languageMenu: "Changer de langue",
      footerMessage: "Publié sous licence Apache-2.0",
      searchButton: "Rechercher dans la documentation",
      searchPlaceholder: "Rechercher dans la documentation",
      searchNoResults: "Aucun résultat pour",
      searchResetButton: "Effacer la recherche",
      searchFooterSelect: "sélectionner",
      searchFooterNavigate: "naviguer",
      searchFooterClose: "fermer",
    },
  },
  {
    path: "de",
    lang: "de",
    label: "Deutsch",
    description:
      "Web-Rendering-Engine für hochperformante Interaktion, virtuelles Scrollen und canvas-native Texteingabe",
    ui: {
      guide: "Leitfaden",
      api: "API",
      playground: "Playground",
      gettingStarted: "Erste Schritte",
      tsx: "TSX",
      architecture: "Architektur",
      scrolling: "Scrollen und Virtualisierung",
      editing: "Text und Bearbeitung",
      events: "Events und Hit-Testing",
      accessibility: "Barrierefreiheit und Tests",
      components: "Komponenten",
      preview: "Vorschau",
      code: "Code",
      previewError: "Vorschau konnte nicht geladen werden",
      styling: "Styling-Leitfaden",
      scssLess: "SCSS und Less",
      cssSupport: "CSS-Unterstützungstabelle",
      migration: "Migration",
      diagnostics: "Diagnose",
      sectionStyling: "Styles",
      sectionBaseElements: "Grundelemente",
      elements: "View, Text und Image",
      elementsEditing: "Eingabe und Bearbeitung",
      elementsSvg: "SVG und Vektorgrafiken",
      widgets: "Widgets",
      groupForm: "Formulare",
      groupLayout: "Layout",
      groupOverlay: "Overlays",
      groupData: "Daten",
      groupFeedback: "Rückmeldung",
      groupProduct: "Produkt",
      sectionStart: "Einstieg",
      sectionCapabilities: "Funktionen",
      sectionShipping: "Betrieb",
      changelog: "Änderungsprotokoll",
      publicApi: "Öffentliche API",
      outline: "Auf dieser Seite",
      returnToTop: "Nach oben",
      appearance: "Erscheinungsbild",
      lastUpdated: "Zuletzt aktualisiert",
      previousPage: "Zurück",
      nextPage: "Weiter",
      menu: "Menü",
      sidebarMenu: "Seitenleistenmenü",
      darkMode: "Dunkelmodus",
      languageMenu: "Sprache wechseln",
      footerMessage: "Veröffentlicht unter der Apache-2.0-Lizenz",
      searchButton: "Dokumentation durchsuchen",
      searchPlaceholder: "Dokumentation durchsuchen",
      searchNoResults: "Keine Ergebnisse für",
      searchResetButton: "Suche zurücksetzen",
      searchFooterSelect: "auswählen",
      searchFooterNavigate: "navigieren",
      searchFooterClose: "schließen",
    },
  },
  {
    path: "ru",
    lang: "ru",
    label: "Русский",
    description:
      "Движок веб-рендеринга для высокопроизводительного взаимодействия, виртуальной прокрутки и нативного редактирования текста на canvas",
    ui: {
      guide: "Руководство",
      api: "API",
      playground: "Playground",
      gettingStarted: "Быстрый старт",
      tsx: "TSX",
      architecture: "Архитектура",
      scrolling: "Прокрутка и виртуализация",
      editing: "Текст и редактирование",
      events: "События и hit-тестирование",
      accessibility: "Доступность и тестирование",
      components: "Компоненты",
      preview: "Предпросмотр",
      code: "Код",
      previewError: "Не удалось загрузить предпросмотр",
      styling: "Руководство по стилям",
      scssLess: "SCSS и Less",
      cssSupport: "Таблица поддержки CSS",
      migration: "Миграция",
      diagnostics: "Диагностика",
      sectionStyling: "Стили",
      sectionBaseElements: "Базовые элементы",
      elements: "View, Text и Image",
      elementsEditing: "Ввод и редактирование",
      elementsSvg: "SVG и векторная графика",
      widgets: "Widgets",
      groupForm: "Формы",
      groupLayout: "Раскладка",
      groupOverlay: "Оверлеи",
      groupData: "Данные",
      groupFeedback: "Обратная связь",
      groupProduct: "Продуктовые",
      sectionStart: "Начало",
      sectionCapabilities: "Возможности",
      sectionShipping: "Выпуск",
      changelog: "История изменений",
      publicApi: "Публичный API",
      outline: "На этой странице",
      returnToTop: "Наверх",
      appearance: "Оформление",
      lastUpdated: "Обновлено",
      previousPage: "Назад",
      nextPage: "Вперёд",
      menu: "Меню",
      sidebarMenu: "Боковое меню",
      darkMode: "Тёмная тема",
      languageMenu: "Выбрать язык",
      footerMessage: "Опубликовано под лицензией Apache-2.0",
      searchButton: "Поиск по документации",
      searchPlaceholder: "Поиск по документации",
      searchNoResults: "Ничего не найдено по запросу",
      searchResetButton: "Очистить запрос",
      searchFooterSelect: "выбрать",
      searchFooterNavigate: "перейти",
      searchFooterClose: "закрыть",
    },
  },
  {
    path: "ar",
    lang: "ar",
    dir: "rtl",
    label: "العربية",
    description:
      "محرّك عرض للويب مصمَّم للتفاعل عالي الأداء والتمرير الافتراضي وتحرير النص أصلاً داخل canvas",
    ui: {
      guide: "الدليل",
      api: "واجهة البرمجة",
      playground: "Playground",
      gettingStarted: "البداية السريعة",
      tsx: "TSX",
      architecture: "نظرة عامة على البنية",
      scrolling: "التمرير والافتراضية",
      editing: "النص والتحرير",
      events: "الأحداث واختبار الإصابة",
      accessibility: "إمكانية الوصول والاختبار",
      components: "المكوّنات",
      preview: "معاينة",
      code: "الشيفرة",
      previewError: "تعذّر تحميل المعاينة",
      styling: "دليل التنسيق",
      scssLess: "SCSS وLess",
      cssSupport: "جدول دعم CSS",
      migration: "الترحيل",
      diagnostics: "التشخيص",
      sectionStyling: "الأنماط",
      sectionBaseElements: "العناصر الأساسية",
      elements: "View وText وImage",
      elementsEditing: "الإدخال والتحرير",
      elementsSvg: "SVG والرسوم المتجهة",
      widgets: "Widgets",
      groupForm: "النماذج",
      groupLayout: "التخطيط",
      groupOverlay: "الطبقات العائمة",
      groupData: "البيانات",
      groupFeedback: "الملاحظات",
      groupProduct: "مكوّنات المنتج",
      sectionStart: "الانطلاق",
      sectionCapabilities: "القدرات",
      sectionShipping: "الإطلاق",
      changelog: "سجل التغييرات",
      publicApi: "واجهة البرمجة العامة",
      outline: "في هذه الصفحة",
      returnToTop: "العودة إلى الأعلى",
      appearance: "المظهر",
      lastUpdated: "آخر تحديث",
      previousPage: "السابق",
      nextPage: "التالي",
      menu: "القائمة",
      sidebarMenu: "القائمة الجانبية",
      darkMode: "الوضع الداكن",
      languageMenu: "تغيير اللغة",
      footerMessage: "منشور برخصة Apache-2.0",
      searchButton: "البحث في التوثيق",
      searchPlaceholder: "البحث في التوثيق",
      searchNoResults: "لا توجد نتائج لـ",
      searchResetButton: "مسح البحث",
      searchFooterSelect: "اختيار",
      searchFooterNavigate: "تنقّل",
      searchFooterClose: "إغلاق",
    },
  },
  {
    path: "he",
    lang: "he",
    dir: "rtl",
    label: "עברית",
    description:
      "מנוע רינדור לרשת עבור אינטראקציה בביצועים גבוהים, גלילה וירטואלית ועריכת טקסט נייטיב בתוך canvas",
    ui: {
      guide: "מדריך",
      api: "API",
      playground: "Playground",
      gettingStarted: "התחלה מהירה",
      tsx: "TSX",
      architecture: "סקירת ארכיטקטורה",
      scrolling: "גלילה ווירטואליזציה",
      editing: "טקסט ועריכה",
      events: "אירועים ובדיקת פגיעה",
      accessibility: "נגישות ובדיקות",
      components: "רכיבים",
      preview: "תצוגה מקדימה",
      code: "קוד",
      previewError: "טעינת התצוגה המקדימה נכשלה",
      styling: "מדריך עיצוב",
      scssLess: "SCSS ו-Less",
      cssSupport: "טבלת תמיכת CSS",
      migration: "הגירה",
      diagnostics: "אבחון",
      sectionStyling: "סגנונות",
      sectionBaseElements: "רכיבי בסיס",
      elements: "View, Text ו-Image",
      elementsEditing: "קלט ועריכה",
      elementsSvg: "SVG וגרפיקה וקטורית",
      widgets: "Widgets",
      groupForm: "טפסים",
      groupLayout: "פריסה",
      groupOverlay: "שכבות צפות",
      groupData: "נתונים",
      groupFeedback: "משוב",
      groupProduct: "רכיבי מוצר",
      sectionStart: "התחלה",
      sectionCapabilities: "יכולות",
      sectionShipping: "עלייה לאוויר",
      changelog: "יומן שינויים",
      publicApi: "API ציבורי",
      outline: "בעמוד זה",
      returnToTop: "חזרה למעלה",
      appearance: "מראה",
      lastUpdated: "עודכן לאחרונה",
      previousPage: "הקודם",
      nextPage: "הבא",
      menu: "תפריט",
      sidebarMenu: "תפריט צד",
      darkMode: "מצב כהה",
      languageMenu: "החלפת שפה",
      footerMessage: "מופץ ברישיון Apache-2.0",
      searchButton: "חיפוש בתיעוד",
      searchPlaceholder: "חיפוש בתיעוד",
      searchNoResults: "לא נמצאו תוצאות עבור",
      searchResetButton: "ניקוי החיפוש",
      searchFooterSelect: "בחירה",
      searchFooterNavigate: "ניווט",
      searchFooterClose: "סגירה",
    },
  },
];
